// Tenant logo upload + preview (PRD-55 §FR-7).
//
// Shared by the School and Institution edit forms, which differ only in
// whether "no logo here" means INHERIT (a school falls back to its
// institution) or NONE (an institution is the top of the chain).
//
// Two decisions here are load-bearing and should not be "cleaned up":
//
// 1. The preview renders on a FIXED LIGHT CHIP in both themes. A great
//    many school crests are dark navy or black, and on the professor
//    app's dark sidebar (`dark:bg-slate-900/80`) such a crest is a black
//    rectangle. Previewing on the same light chip the app renders on is
//    the only way the director sees the truth. A `logo_dark_path` column
//    was the alternative and was rejected as disproportionate.
//
// 2. The preview box is NOT square. A school crest is as often a 4:1
//    wordmark as a shield, and a wordmark in a square box renders as a
//    smear. `object-contain` inside a fixed-height, flexible-width box
//    matches what the professor app actually does.

import { Button } from "@components/ui/Button"
import { ConfirmDialog } from "@components/ui/ConfirmDialog"
import { cn } from "@lib/cn"
import { logger } from "@lib/logger"
import { ImageOff, Trash2, Upload } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  BRANDING_ACCEPTED_MIME,
  BRANDING_ACCEPT_ATTRIBUTE,
  BRANDING_MAX_BYTES,
  BRANDING_MAX_KB,
} from "@/constants/branding"

interface Props {
  /** The crest actually displayed today, resolved (may be inherited). */
  logoUrl: string | null | undefined
  /** Whether this row sets its OWN crest, vs. inheriting one. */
  isOwnLogo: boolean
  /** Name of the parent whose crest is inherited, when one is. */
  inheritedFrom?: string
  onUpload: (file: File) => Promise<unknown>
  onRemove: () => Promise<unknown>
  busy?: boolean
  disabled?: boolean
  /** A section of its own (a detail page) drops the rule that separates it
   *  from the form above it in a dialog. */
  standalone?: boolean
}

export function LogoField({
  logoUrl,
  isOwnLogo,
  inheritedFrom,
  onUpload,
  onRemove,
  busy,
  disabled,
  standalone,
}: Props) {
  // `common`, not a feature namespace: this component lives in
  // components/ and is used by both the School and Institution forms,
  // so borrowing `schools` keys here would be exactly the feature
  // knowledge architecture.md forbids a shared component from having.
  const { t } = useTranslation(["common", "errors"])
  const inputRef = useRef<HTMLInputElement>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [clientError, setClientError] = useState<string | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  // PRD-56 §FR-21 — removing the crest changes every PDF and page that
  // shows it, and saves on the spot: it is confirmed first.
  const [confirmingRemove, setConfirmingRemove] = useState(false)

  // A blob URL is a document-lifetime resource; without the revoke it
  // leaks for as long as the tab lives, and nothing ever surfaces it.
  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview)
    }
  }, [preview])

  const shown = preview ?? (loadFailed ? null : (logoUrl ?? null))

  const handleFile = async (file: File | undefined) => {
    if (!file) return
    setClientError(null)

    // Mirrors the server's allowlist and cap (see @/constants/branding).
    // This is a courtesy — a fast, specific message instead of a
    // round-trip — never the gate. The server re-checks everything, and
    // it checks the actual BYTES, which the browser cannot vouch for.
    if (!(BRANDING_ACCEPTED_MIME as readonly string[]).includes(file.type)) {
      setClientError(t("errors:branding_unsupported_mime"))
      return
    }
    if (file.size > BRANDING_MAX_BYTES) {
      setClientError(t("errors:branding_image_too_large"))
      return
    }

    const objectUrl = URL.createObjectURL(file)
    setPreview(objectUrl)
    setLoadFailed(false)

    try {
      await onUpload(file)
    } catch {
      // The caller toasts + logs; here we only roll the optimistic
      // preview back so the box does not keep showing a crest the
      // server refused.
      URL.revokeObjectURL(objectUrl)
      setPreview(null)
    } finally {
      // Reset so re-picking the SAME file fires onChange again.
      if (inputRef.current) inputRef.current.value = ""
    }
  }

  const handleRemove = async () => {
    setClientError(null)
    try {
      await onRemove()
      if (preview) URL.revokeObjectURL(preview)
      setPreview(null)
      setLoadFailed(false)
      setConfirmingRemove(false)
    } catch (err) {
      logger.error("branding.remove.failed", { error: String(err) })
    }
  }

  return (
    // PRD-60 chunk B: a section of the form, not a tinted card inside it. It
    // says it saves on its own — the rest of the form waits for "Guardar
    // cambios", and a director could not tell the two apart.
    <fieldset className={cn("space-y-3", !standalone && "border-hairline border-t pt-4")}>
      <legend className="text-ink float-left mb-1 w-full text-sm font-semibold">
        {t("branding.title")}
      </legend>
      <p className="text-meta clear-left">
        {t("branding.help", { kb: BRANDING_MAX_KB })} {t("branding.saves_now")}
      </p>

      <div className="flex flex-wrap items-center gap-4">
        {/* Fixed LIGHT chip in both themes — see the header note. The one
            place a literal white is the point: a role token would turn dark. */}
        <div
          className={cn(
            "rounded-control flex h-14 w-32 shrink-0 items-center justify-center border p-2",
            // The empty box is not a preview, so it follows the theme.
            shown ? "border-border bg-white" : "border-border bg-inset border-dashed",
          )}
        >
          {shown ? (
            <img
              src={shown}
              alt=""
              aria-hidden="true"
              className="max-h-full max-w-full object-contain"
              onError={() => setLoadFailed(true)}
            />
          ) : (
            <ImageOff className="text-ink-subtle h-5 w-5" aria-hidden="true" />
          )}
        </div>

        <div className="min-w-0 space-y-2">
          <p className="text-ink-muted text-sm">
            {isOwnLogo
              ? t("branding.state.own")
              : shown && inheritedFrom
                ? t("branding.state.inherited", { institution: inheritedFrom })
                : t("branding.state.none")}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              leftIcon={<Upload className="h-4 w-4" />}
              loading={busy}
              disabled={disabled}
              onClick={() => inputRef.current?.click()}
            >
              {isOwnLogo ? t("branding.actions.replace") : t("branding.actions.upload")}
            </Button>
            {isOwnLogo && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                leftIcon={<Trash2 className="h-4 w-4" />}
                disabled={disabled || busy}
                onClick={() => setConfirmingRemove(true)}
              >
                {t("branding.actions.remove")}
              </Button>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmingRemove}
        title={t("branding.remove_confirm.title")}
        message={
          inheritedFrom
            ? t("branding.remove_confirm.inherit", { institution: inheritedFrom })
            : t("branding.remove_confirm.none")
        }
        confirmLabel={t("branding.actions.remove")}
        loading={busy}
        onConfirm={() => void handleRemove()}
        onClose={() => setConfirmingRemove(false)}
      />

      <input
        ref={inputRef}
        type="file"
        accept={BRANDING_ACCEPT_ATTRIBUTE}
        className="sr-only"
        aria-label={t("branding.actions.upload")}
        onChange={(event) => void handleFile(event.target.files?.[0])}
      />

      {clientError && (
        <p role="alert" className="text-danger-ink text-xs">
          {clientError}
        </p>
      )}
    </fieldset>
  )
}
